import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import PageTitle from '../components/PageTitle';

const AdminLeaguesPage = () => {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [newLeague, setNewLeague] = useState({
    name: '',
    store: '',
    startDate: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingLeague, setEditingLeague] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Mock data
  const stores = [
    { id: 1, name: 'Tienda 1', logo: '🏪' },
    { id: 2, name: 'Tienda 2', logo: '🏬' },
  ];

  // Mock initial leagues
  useEffect(() => {
    setLeagues([
      {
        id: 1,
        name: 'Liga Verano 2024',
        store: 'Tienda 1',
        startDate: '2024-06-01',
        status: 'upcoming',
        participants: 0
      },
    ]);
  }, []);

  const handleBack = () => {
    navigate('/admin');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newLeague.name || !newLeague.store || !newLeague.startDate) {
      setError('Por favor completa todos los campos');
      return;
    }

    // Mock adding/editing a league
    if (editingLeague) {
      setLeagues(leagues.map(league => 
        league.id === editingLeague.id 
          ? { ...league, ...newLeague }
          : league
      ));
      setSuccess('Liga actualizada exitosamente');
    } else {
      const newLeagueData = {
        id: leagues.length + 1,
        ...newLeague,
        status: 'upcoming',
        participants: 0
      };
      setLeagues([...leagues, newLeagueData]);
      setSuccess('Liga creada exitosamente');
    }

    setNewLeague({ name: '', store: '', startDate: '' });
    setEditingLeague(null);
    setError(null);
    setOpenDialog(false);

    setTimeout(() => setSuccess(null), 3000);
  };

  const handleEdit = (league) => {
    setEditingLeague(league);
    setNewLeague({
      name: league.name,
      store: league.store,
      startDate: league.startDate,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    setLeagues(leagues.filter(league => league.id !== id));
    setSuccess('Liga eliminada exitosamente');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setEditingLeague(null);
    setNewLeague({ name: '', store: '', startDate: '' });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLeague(null);
    setNewLeague({ name: '', store: '', startDate: '' });
    setError(null);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'primary.main', color: 'white' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <PageTitle title="Gestión de Ligas" />
        </Box>

        {/* Leagues List Section */}
        <SectionTitle 
          title="Ligas" 
          subtitle={`Total: ${leagues.length} ligas`}
          headerContent={
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                bgcolor: '#c89e38',
                color: 'white',
                '&:hover': { bgcolor: '#b38d2d' }
              }}
            >
              Nueva Liga
            </Button>
          }
        >
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Liga</TableCell>
                  <TableCell>Tienda</TableCell>
                  <TableCell>Fecha de Inicio</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Participantes</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leagues.map((league) => (
                  <TableRow key={league.id}>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {league.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon sx={{ color: 'text.secondary' }} />
                        {league.store}
                      </Box>
                    </TableCell>
                    <TableCell>{league.startDate}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: league.status === 'active' ? 'success.main' : 'text.secondary',
                          fontWeight: 'medium'
                        }}
                      >
                        {league.status === 'upcoming' ? 'Próximamente' : 
                         league.status === 'active' ? 'Activa' : 'Finalizada'}
                      </Typography>
                    </TableCell>
                    <TableCell>{league.participants}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton 
                          size="small"
                          onClick={() => handleEdit(league)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleDelete(league.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionTitle>
      </Stack>

      {/* Create/Edit League Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLeague ? 'Editar Liga' : 'Nueva Liga'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre de la Liga"
                  value={newLeague.name}
                  onChange={(e) => setNewLeague({ ...newLeague, name: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tienda</InputLabel>
                  <Select
                    value={newLeague.store}
                    label="Tienda"
                    onChange={(e) => setNewLeague({ ...newLeague, store: e.target.value })}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{store.logo}</Typography>
                          {store.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fecha de Inicio"
                  type="date"
                  value={newLeague.startDate}
                  onChange={(e) => setNewLeague({ ...newLeague, startDate: e.target.value })}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#c89e38',
              '&:hover': { bgcolor: '#b38d2d' }
            }}
          >
            {editingLeague ? 'Guardar Cambios' : 'Crear Liga'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLeaguesPage; 